import { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import { Box, ButtonBase, SvgIcon } from '@mui/material';

import { useTheme } from '../styles/styles';
import { NavigationProps, RouteConfigItemType } from './types';

// Side menu (vs. a tab row) once the tabs area is at least this wide.
export const SIDE_MENU_QUERY = '@container (min-width: 860px)';

const doc = (
  <>
    <path d="M7 3h7l5 5v13H7z" />
    <path d="M14 3v5h5M10 13h6M10 17h6" />
  </>
);

const SECTION_ICONS: Record<string, ReactNode> = {
  main: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 20c1.5-4 4.5-6 8-6s6.5 2 8 6" />
    </>
  ),
  rates: (
    <path d="M12 3v18M16.5 7.5c-.8-1.3-2.5-2-4.5-2-2.5 0-4 1.2-4 3s1.6 2.6 4 3 4.5 1.3 4.5 3.2-1.8 3.3-4.5 3.3c-2.2 0-3.9-.8-4.7-2.2" />
  ),
  clocks: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </>
  ),
  status: <path d="M3 12h4l3-7 4 14 3-7h4" />,
  workPattern: (
    <>
      <rect x="4" y="4" width="7" height="7" rx="1.5" />
      <rect x="13" y="4" width="7" height="7" rx="1.5" />
      <rect x="4" y="13" width="7" height="7" rx="1.5" />
      <rect x="13" y="13" width="7" height="7" rx="1.5" />
    </>
  ),
  schedulePattern: (
    <>
      <rect x="4" y="5" width="16" height="15" rx="2" />
      <path d="M4 10h16M9 3v4M15 3v4" />
    </>
  ),
  history: (
    <>
      <path d="M4.5 9A8 8 0 1 1 4 12M4 4v5h5" />
      <path d="M12 8v4l3 2" />
    </>
  ),
  general: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 11v5M12 8h.01" />
    </>
  ),
  contact: (
    <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a1 1 0 0 1-1 1A16 16 0 0 1 4 5a1 1 0 0 1 1-1z" />
  ),
  health: (
    <path d="M12 20s-7-4.5-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.5-7 10-7 10z" />
  ),
  i9Documents: doc,
  licenses: (
    <>
      <circle cx="12" cy="9" r="5" />
      <path d="M9 13.5L8 21l4-2 4 2-1-7.5" />
    </>
  ),
  journal: (
    <>
      <path d="M4 5a2 2 0 0 1 2-2h13v16H6a2 2 0 0 0-2 2z" />
      <path d="M4 21V5" />
    </>
  ),
  inService: (
    <>
      <path d="M2 9l10-5 10 5-10 5z" />
      <path d="M6 11v5c3 2 9 2 12 0v-5" />
    </>
  ),
  misc: (
    <>
      <circle cx="6" cy="12" r="1.3" />
      <circle cx="12" cy="12" r="1.3" />
      <circle cx="18" cy="12" r="1.3" />
    </>
  ),
  authorization: <path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6z" />,
  folder: (
    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
  ),
};

const iconFor = (item: RouteConfigItemType) =>
  item.icon ? SECTION_ICONS[item.path] ?? doc : null;

export const NavigationMenu = ({ items, onChangeSubMenu }: NavigationProps) => {
  const theme = useTheme();
  const location = useLocation();

  const isSelected = (item: RouteConfigItemType) => {
    const pathname = location.pathname;
    const isMainRoute = item.path === 'main';
    return (
      pathname.includes(item.path) ||
      (pathname.endsWith('accruals') && isMainRoute)
    );
  };

  // While the employee loads there are no sections yet; don't draw an
  // empty menu frame.
  if (!items.length) return null;

  return (
    <Box
      component="nav"
      aria-label="Sections"
      sx={{
        display: 'flex',
        flexDirection: 'row',
        gap: '1px',
        flexShrink: 0,
        overflowX: 'auto',
        [SIDE_MENU_QUERY]: {
          flexDirection: 'column',
          width: 184,
          alignSelf: 'flex-start',
          maxHeight: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
          '& > button': { whiteSpace: 'normal' },
        },
        padding: '6px',
        borderRadius: '12px',
        border: `1px solid ${theme.custom?.shellSetupNavBorder}`,
        background: theme.custom?.shellSetupNavBg,
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      {items.map((item) => {
        const selected = isSelected(item);
        const icon = iconFor(item);
        return (
          <ButtonBase
            key={item.path}
            onClick={() => onChangeSubMenu?.(item.path)}
            aria-current={selected ? 'page' : undefined}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: '8px',
              flexShrink: 0,
              minHeight: 32,
              padding: '6px 9px',
              borderRadius: '7px',
              border: '1px solid transparent',
              textAlign: 'left',
              whiteSpace: 'nowrap',
              fontSize: '13px',
              lineHeight: '18px',
              fontWeight: selected ? 600 : 400,
              color: selected
                ? theme.custom?.brandNavy
                : theme.custom?.shellMenuItemText,
              background: selected ? theme.background.white : 'transparent',
              boxShadow: selected
                ? theme.custom?.shellSetupNavActiveShadow
                : 'none',
              '&:hover': {
                color: theme.custom?.brandNavy,
                background: selected
                  ? theme.background.white
                  : theme.custom?.shellMenuItemHoverBg,
              },
            }}
          >
            {icon && (
              <SvgIcon
                viewBox="0 0 24 24"
                aria-hidden
                sx={{
                  width: 21,
                  height: 21,
                  margin: '-3px -1px -3px -3px',
                  padding: '3px',
                  borderRadius: '6px',
                  flexShrink: 0,
                  fill: 'none',
                  stroke: 'currentColor',
                  strokeWidth: 1.7,
                  strokeLinecap: 'round',
                  strokeLinejoin: 'round',
                  color: selected
                    ? theme.background.white
                    : theme.custom?.shellNavChevron,
                  background: selected
                    ? theme.colors.primary.main
                    : 'transparent',
                }}
              >
                {icon}
              </SvgIcon>
            )}
            <Box
              component="span"
              sx={{ minWidth: 0, overflowWrap: 'anywhere' }}
            >
              {item.label}
            </Box>
          </ButtonBase>
        );
      })}
    </Box>
  );
};
